import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Eye, Edit, Trash2, FileText, Clock, AlertCircle, ChevronLeft, ChevronRight, X, History, BarChart3, ClipboardList } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useQuery } from '@tanstack/react-query';
import { getStatusPipeline } from '../services/statusPipelineService';
import surveyService from '../services/surveyService';
import auditService from '../services/auditService';
import SurveyForm from './SurveyForm';
import SurveyView from './SurveyView';
import StatusChangeModal from './StatusChangeModal';
import BudgetSheetsManager from './BudgetSheetsManager';
import ConfirmModal from './ConfirmModal';
import toast from 'react-hot-toast';

const SurveyListSimple = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [surveys, setSurveys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [showView, setShowView] = useState(false);
  const [selectedSurvey, setSelectedSurvey] = useState(null);
  const [editingSurvey, setEditingSurvey] = useState(null);
  const [stats, setStats] = useState({});
  const [changingStatusId, setChangingStatusId] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [statusModalSurvey, setStatusModalSurvey] = useState(null);
  const [statusModalNewStatus, setStatusModalNewStatus] = useState(null);
  const [show3DAnalyticsModal, setShow3DAnalyticsModal] = useState(false);
  const [analytics3DData, setAnalytics3DData] = useState(null);
  const [showBudgetSheetModal, setShowBudgetSheetModal] = useState(false);
  const [budgetSheetSurvey, setBudgetSheetSurvey] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmSurvey, setDeleteConfirmSurvey] = useState(null);
  const [showMassDeleteConfirm, setShowMassDeleteConfirm] = useState(false);

  const { data: statusPipeline } = useQuery({
    queryKey: ['status-pipeline', 'survey'],
    queryFn: () => getStatusPipeline('SURVEY').then(res => res.data?.status_config),
    staleTime: Infinity,
  });

  const getNextStatuses = (currentStatus) => {
    if (!statusPipeline || !currentStatus) return [];
    const current = statusPipeline[currentStatus];
    if (!current || !current.next || current.next.length === 0) return [currentStatus];
    return current.next;
  };

  // Estados de paginação
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  // Estados de seleção múltipla
  const [selectedIds, setSelectedIds] = useState(new Set());

  useEffect(() => {
    loadSurveys(1);
    loadStats();
  }, []);

  const loadSurveys = async (page = pagination.page) => {
    try {
      setLoading(true);
      const response = await surveyService.getAllSurveys({ page, limit: pagination.limit });
      setSurveys(response.data || []);

      // Atualizar informações de paginação
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          page: response.pagination.page,
          total: response.pagination.total,
          pages: response.pagination.pages
        }));
      }
    } catch (error) {
      console.error('❌ Erro ao carregar levantamentos:', error);
      toast.error('Erro ao carregar levantamentos: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await surveyService.getSurveyStats();
      setStats(response.data || {});
    } catch (error) {
      // Não mostrar erro para stats, não é crítico
    }
  };

  const handleStatusChange = (surveyId, newStatus) => {
    const survey = surveys.find(s => s.id === surveyId);
    setStatusModalSurvey(survey);
    setStatusModalNewStatus(newStatus);
    setShowStatusModal(true);
  };

  const handleView3DAnalytics = async (survey) => {
    try {
      const response = await surveyService.get3DAnalytics(survey.id);
      setAnalytics3DData(response.data);
      setShow3DAnalyticsModal(true);
    } catch (error) {
      toast.error('Erro ao carregar analytics: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleStatusModalConfirm = async (extraData) => {
    if (!statusModalSurvey || !statusModalNewStatus) return;

    let updateData = { status: statusModalNewStatus, ...extraData };

    if (statusModalNewStatus === 'EM_PROJETACAO') {
      updateData.project_3d_status = 'IN_PROGRESS';
    }

    try {
      setChangingStatusId(statusModalSurvey.id);
      setShowStatusModal(false);
      await surveyService.updateSurvey(statusModalSurvey.id, updateData);
      await loadSurveys(pagination.page);
      toast.success(`Status alterado para "${surveyService.formatStatus(statusModalNewStatus)}"`);
    } catch (error) {
      console.error('Erro ao mudar status:', error);
      toast.error(error.response?.data?.message || 'Erro ao mudar status');
    } finally {
      setChangingStatusId(null);
      setStatusModalSurvey(null);
      setStatusModalNewStatus(null);
    }
  };

  const handleStatusModalClose = () => {
    setShowStatusModal(false);
    setStatusModalSurvey(null);
    setStatusModalNewStatus(null);
  };

  const handleCreate = () => {
    setEditingSurvey(null);
    setShowForm(true);
  };

  const handleEdit = async (survey) => {
    try {
      setLoading(true);
      // Buscar dados completos do levantamento
      const response = await surveyService.getSurveyById(survey.id);
      setEditingSurvey(response.data);
      setShowForm(true);
    } catch (error) {
      console.error('Erro ao carregar levantamento para edição:', error);
      toast.error('Erro ao carregar dados do levantamento');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenBudgetSheet = (survey) => {
    setBudgetSheetSurvey(survey);
    setShowBudgetSheetModal(true);
  };

  const handleView = async (survey) => {
    try {
      setLoading(true);

      // Carregar dados completos do levantamento (incluindo arquivos)
      const response = await surveyService.getSurveyById(survey.id);

      // Debug: ver estrutura da resposta
      console.log('📦 Response structure:', JSON.stringify(response, null, 2));

      // A resposta do service já é response.data (que contém { success: true, data: survey })
      // Então response.data.data é undefined - a survey está em response.data
      const fullSurveyData = response.data;

      if (!fullSurveyData) {
        console.error('❌ fullSurveyData is undefined. response:', response);
        throw new Error('Dados do levantamento não encontrados');
      }

      // Garantir que campos importantes estejam presentes
      const surveyData = {
        ...fullSurveyData,
        created_by: fullSurveyData.created_by || survey.created_by,
        project_3d_observations: fullSurveyData.project_3d_observations || survey.project_3d_observations || '',
        project_3d_status: fullSurveyData.project_3d_status || survey.project_3d_status,
        project_3d_created_at: fullSurveyData.project_3d_created_at || survey.project_3d_created_at,
        project_3d_updated_at: fullSurveyData.project_3d_updated_at || survey.project_3d_updated_at
      };
      
      setSelectedSurvey(surveyData);
      setShowView(true);
    } catch (error) {
      console.error('❌ Erro ao carregar levantamento:', error);
      toast.error('Erro ao carregar levantamento: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (survey) => {
    if (user.role !== 'GERENCIA') {
      toast.error('Apenas CEO pode deletar levantamentos');
      return;
    }

    setDeleteConfirmSurvey(survey);
    setShowDeleteConfirm(true);
    return;
  };

  const handleDeleteConfirm = async () => {
    const survey = deleteConfirmSurvey;
    setShowDeleteConfirm(false);
    setDeleteConfirmSurvey(null);

    if (!survey) return;

    try {
      await surveyService.deleteSurvey(survey.id);
      toast.success('Levantamento deletado com sucesso');
      loadSurveys(pagination.page);
      loadStats();
    } catch (error) {
      toast.error('Erro ao deletar levantamento: ' + (error.response?.data?.message || error.message));
    }
  };

  const handleFormSuccess = () => {
    setShowForm(false);
    setEditingSurvey(null);
    loadSurveys(1);
    loadStats();
    toast.success('Levantamento salvo com sucesso!');
  };

  // Handlers de paginação
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.pages) {
      loadSurveys(newPage);
    }
  };

  const handleLimitChange = (newLimit) => {
    setPagination(prev => ({ ...prev, limit: parseInt(newLimit) }));
    loadSurveys(1);
  };

  // Seleção múltipla
  const handleSelectAll = () => {
    if (selectedIds.size === surveys.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(surveys.map(s => s.id)));
    }
  };

  const handleSelectOne = (id) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(id)) {
      newSelected.delete(id);
    } else {
      newSelected.add(id);
    }
    setSelectedIds(newSelected);
  };

  // Exclusão em massa
  const handleMassDelete = async () => {
    if (selectedIds.size === 0) {
      toast.error('Nenhum levantamento selecionado');
      return;
    }

    setShowMassDeleteConfirm(true);
  };

  const handleMassDeleteConfirm = async () => {
    setShowMassDeleteConfirm(false);

    try {
      await auditService.deleteMultipleSurveys(Array.from(selectedIds));
      toast.success(`${selectedIds.size} levantamentos excluídos com sucesso`);
      setSelectedIds(new Set());
      loadSurveys(pagination.page);
      loadStats();
    } catch (error) {
      toast.error('Erro ao excluir: ' + (error.response?.data?.message || error.message));
    }
  };

  // Perfis executivos e recepção
  const isManagement = ['GERENCIA', 'ADMIN', 'CEO'].includes(user?.role) || user?.permissions?.includes('all:full');
  const canCreate = isManagement;
  const canEdit = isManagement || user?.role === 'RECEPCAO_PROJETOS';
  const canDelete = isManagement;

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-4 sm:mb-6">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900">
              🏠 Sistema de Levantamento
            </h1>
            <p className="text-gray-600 text-sm sm:text-base hidden sm:block">
              Gerenciamento de levantamentos na casa do cliente
            </p>
            <p className="text-xs sm:text-sm text-blue-600 mt-1">
              Usuário: {user.full_name} ({user.role})
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {canDelete && (
              <button
                onClick={() => navigate('/auditoria')}
                className="bg-gray-600 text-white px-3 sm:px-4 py-2 sm:py-3 rounded-lg hover:bg-gray-700 flex items-center gap-2 text-sm sm:text-base"
              >
                <History className="w-4 h-4" />
                <span className="hidden sm:inline">Auditoria</span>
              </button>
            )}
            {canCreate && (
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg hover:bg-blue-700 flex items-center gap-2 font-medium shadow-lg text-sm sm:text-base"
              >
                <Plus className="w-4 h-4" />
                Novo Levantamento
              </button>
            )}
          </div>
        </div>

        {/* Estatísticas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
          <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-sm text-gray-600">Total</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total_surveys || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-sm text-gray-600">Rascunhos</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.draft_count || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-green-50 p-4 rounded-lg border border-green-200">
            <div className="flex items-center gap-3">
              <FileText className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-sm text-gray-600">Concluídos</p>
                <p className="text-2xl font-bold text-green-600">{stats.completed_count || 0}</p>
              </div>
            </div>
          </div>
          
          <div className="bg-red-50 p-4 rounded-lg border border-red-200">
            <div className="flex items-center gap-3">
              <AlertCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-sm text-gray-600">Alta Prioridade</p>
                <p className="text-2xl font-bold text-red-600">{stats.high_priority_count || 0}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Levantamentos */}
      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">Levantamentos Recentes</h2>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-2">Carregando levantamentos...</p>
          </div>
        ) : surveys.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <FileText className="w-12 h-12 mx-auto mb-4 text-gray-300" />
            <p className="text-lg mb-2">Nenhum levantamento encontrado</p>
            <p className="text-sm mb-4">Comece criando seu primeiro levantamento</p>
            {canCreate && (
              <button
                onClick={handleCreate}
                className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700"
              >
                Criar Primeiro Levantamento
              </button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto max-w-full">
            <table className="w-full min-w-[800px]">
              <thead className="bg-gray-50">
                <tr>
                  {canDelete && (
                    <th className="px-2 py-2 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                      <input
                        type="checkbox"
                        checked={selectedIds.size === surveys.length && surveys.length > 0}
                        onChange={handleSelectAll}
                        className="w-4 h-4 rounded border-gray-300"
                        title={selectedIds.size === surveys.length ? "Desmarcar todos" : "Selecionar todos"}
                      />
                    </th>
                  )}
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Código
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Cliente
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Prioridade
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Data
                  </th>
                  <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                    Responsável
                  </th>
                  <th className="px-2 py-2 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Ações
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {surveys.map((survey) => (
                  <tr key={survey.id} className={`hover:bg-gray-50 ${selectedIds.has(survey.id) ? 'bg-red-50' : ''}`}>
                    {canDelete && (
                      <td className="px-2 py-3 whitespace-nowrap text-center">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(survey.id)}
                          onChange={() => handleSelectOne(survey.id)}
                          className="w-4 h-4 rounded border-gray-300"
                        />
                      </td>
                    )}
                    <td className="px-3 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {survey.survey_code || `LEV-${survey.id}`}
                      </div>
                    </td>
                    
                    <td className="px-3 py-3">
                      <div className="text-sm font-medium text-gray-900 truncate max-w-[140px]">
                        {survey.client_name}
                      </div>
                      <div className="text-sm text-gray-500 truncate max-w-[140px]">
                        {survey.client_address?.substring(0, 40)}...
                      </div>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap">
                      {user.role === 'GERENCIA' ? (
                        <select
                          value={survey.status}
                          onChange={(e) => handleStatusChange(survey.id, e.target.value)}
                          disabled={changingStatusId === survey.id}
                          className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full border-0 cursor-pointer focus:ring-2 focus:ring-blue-500 ${surveyService.getStatusColor(survey.status)}`}
                        >
                          <option value={survey.status}>
                            {surveyService.formatStatus(survey.status)}
                          </option>
                          {getNextStatuses(survey.status).map((nextStatus) => (
                            <option key={nextStatus} value={nextStatus}>
                              {surveyService.formatStatus(nextStatus)}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${surveyService.getStatusColor(survey.status)}`}>
                          {surveyService.formatStatus(survey.status)}
                        </span>
                      )}
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${surveyService.getPriorityColor(survey.priority)}`}>
                        {surveyService.formatPriority(survey.priority)}
                      </span>
                    </td>
                    
                    <td className="px-3 py-3 whitespace-nowrap text-sm text-gray-500">
                      {surveyService.formatDate(survey.survey_date || survey.created_at)}
                    </td>

                    <td className="px-3 py-3 whitespace-nowrap hidden lg:table-cell">
                      <div className="text-sm text-gray-900 truncate max-w-[100px]">
                        {survey.status === 'EM_PROJETACAO' && survey.project_3d_created_by_name
                          ? survey.project_3d_created_by_name
                          : survey.current_responsible_name || survey.created_by_name || '-'}
                      </div>
                    </td>

                    <td className="px-2 py-3 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleView(survey)}
                          className="text-blue-600 hover:text-blue-900 p-2 rounded"
                          title="Visualizar"
                        >
                          <Eye className="w-5 h-5" />
                        </button>

                        {canEdit && (
                          <button
                            onClick={() => handleEdit(survey)}
                            className="text-green-600 hover:text-green-900 p-2 rounded"
                            title="Editar"
                          >
                            <Edit className="w-5 h-5" />
                          </button>
                        )}

                        {user.role === 'GERENCIA' && survey.project_3d_status && (
                          <button
                            onClick={() => handleView3DAnalytics(survey)}
                            className="text-purple-600 hover:text-purple-900 p-2 rounded"
                            title="Analytics 3D"
                          >
                            <BarChart3 className="w-5 h-5" />
                          </button>
                        )}

                        {(survey.status === 'AGUARDANDO_APRESENTACAO' || survey.status === 'APRESENTACAO_AGENDADA' || survey.status === 'APRESENTADO_CLIENTE') && (
                          <button
                            onClick={() => handleOpenBudgetSheet(survey)}
                            className="text-orange-600 hover:text-orange-900 p-2 rounded"
                            title="Ficha de Orçamento"
                          >
                            <ClipboardList className="w-5 h-5" />
                          </button>
                        )}

                        {canDelete && (
                          <button
                            onClick={() => handleDelete(survey)}
                            className="text-red-600 hover:text-red-900 p-2 rounded"
                            title="Deletar"
                          >
                            <Trash2 className="w-5 h-5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Barra de Ações em Massa (apenas GERENCIA) */}
      {canDelete && selectedIds.size > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <span className="text-sm font-medium text-red-800">
                {selectedIds.size} levantamento(s) selecionado(s)
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={handleMassDelete}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 flex items-center gap-2 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Excluir Selecionados
                </button>
                <button
                  onClick={() => setSelectedIds(new Set())}
                  className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 flex items-center gap-2 text-sm"
                >
                  <X className="w-4 h-4" />
                  Cancelar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Controles de Paginação */}
      {!loading && surveys.length > 0 && (
        <div className="bg-white rounded-lg shadow px-4 py-3 sm:px-6 sm:py-4">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-xs sm:text-sm text-gray-600">Itens:</span>
              <select
                value={pagination.limit}
                onChange={(e) => handleLimitChange(e.target.value)}
                className="border border-gray-300 rounded px-2 py-1 text-sm"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className="flex items-center justify-between sm:justify-end gap-4">
              <span className="text-xs sm:text-sm text-gray-600">
                {pagination.page}/{pagination.pages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page <= 1}
                  className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Página anterior"
                >
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.pages}
                  className="p-2 rounded hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Próxima página"
                >
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modais */}
      {showForm && (
        <SurveyForm
          survey={editingSurvey}
          onClose={() => {
            setShowForm(false);
            setEditingSurvey(null);
          }}
          onSuccess={handleFormSuccess}
        />
      )}

      {showView && selectedSurvey && (
        <SurveyView
          survey={selectedSurvey}
          onClose={() => {
            setShowView(false);
            setSelectedSurvey(null);
          }}
          onEdit={() => {
            setShowView(false);
            handleEdit(selectedSurvey);
          }}
        />
      )}

      <StatusChangeModal
        show={showStatusModal}
        survey={statusModalSurvey}
        newStatus={statusModalNewStatus}
        onClose={handleStatusModalClose}
        onConfirm={handleStatusModalConfirm}
        isLoading={changingStatusId !== null}
      />

      {show3DAnalyticsModal && analytics3DData && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
                <BarChart3 className="w-6 h-6 text-purple-600" />
                Analytics Projeto 3D
              </h2>
              <button
                onClick={() => setShow3DAnalyticsModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
              <div className="mb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-1">{analytics3DData.surveyCode}</h3>
                <p className="text-sm text-gray-500">{analytics3DData.clientName}</p>
              </div>

              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="bg-purple-50 rounded-lg p-4 border border-purple-200">
                  <p className="text-xs text-purple-600 font-medium mb-1">Estimadas</p>
                  <p className="text-2xl font-bold text-purple-700">{analytics3DData.estimatedHours.toFixed(2)}h</p>
                </div>
                <div className="bg-green-50 rounded-lg p-4 border border-green-200">
                  <p className="text-xs text-green-600 font-medium mb-1">Trabalhadas</p>
                  <p className="text-2xl font-bold text-green-700">{analytics3DData.totalWorked.toFixed(2)}h</p>
                </div>
                <div className={`rounded-lg p-4 border ${analytics3DData.diff >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-red-50 border-red-200'}`}>
                  <p className={`text-xs font-medium mb-1 ${analytics3DData.diff >= 0 ? 'text-blue-600' : 'text-red-600'}`}>Diferença</p>
                  <p className={`text-2xl font-bold ${analytics3DData.diff >= 0 ? 'text-blue-700' : 'text-red-700'}`}>
                    {analytics3DData.diff >= 0 ? '+' : ''}{analytics3DData.diff.toFixed(2)}h
                  </p>
                </div>
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
                  <p className="text-xs text-gray-600 font-medium mb-1">Dias</p>
                  <p className="text-2xl font-bold text-gray-700">{analytics3DData.dailyDetails.length}</p>
                </div>
              </div>

              <h4 className="text-sm font-semibold text-gray-900 mb-3">Detalhamento por Dia</h4>
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Data</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Agendado</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Trabalhado</th>
                      <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {analytics3DData.dailyDetails.map((day, index) => (
                      <tr key={index}>
                        <td className="px-4 py-2 text-sm text-gray-900">{day.date}</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right">{day.scheduledHours.toFixed(2)}h</td>
                        <td className="px-4 py-2 text-sm text-gray-900 text-right font-medium">{day.workedHours.toFixed(2)}h</td>
                        <td className="px-4 py-2 text-right">
                          <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                            day.status === 'COMPLETED' ? 'bg-green-100 text-green-800' :
                            day.status === 'IN_PROGRESS' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-800'
                          }`}>
                            {day.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Ficha de Orçamento */}
      {showBudgetSheetModal && budgetSheetSurvey && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-6 border-b">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Fichas de Orçamento</h2>
                <p className="text-sm text-gray-500">{budgetSheetSurvey.client_name} - {budgetSheetSurvey.survey_code}</p>
              </div>
              <button
                onClick={() => {
                  setShowBudgetSheetModal(false);
                  setBudgetSheetSurvey(null);
                }}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              <BudgetSheetsManager survey={budgetSheetSurvey} />
            </div>
          </div>
        </div>
      )}

      <ConfirmModal
        show={showDeleteConfirm}
        onConfirm={handleDeleteConfirm}
        onCancel={() => {
          setShowDeleteConfirm(false);
          setDeleteConfirmSurvey(null);
        }}
        title="Confirmar Exclusão"
        message={`Tem certeza que deseja deletar o levantamento ${deleteConfirmSurvey?.survey_code}?`}
        confirmText="Deletar"
        cancelText="Cancelar"
        type="danger"
      />

      <ConfirmModal
        show={showMassDeleteConfirm}
        onConfirm={handleMassDeleteConfirm}
        onCancel={() => setShowMassDeleteConfirm(false)}
        title="Confirmar Exclusão em Massa"
        message={`Tem certeza que deseja excluir ${selectedIds.size} levantamentos?`}
        confirmText="Excluir Todos"
        cancelText="Cancelar"
        type="danger"
      />
    </div>
  );
};

export default SurveyListSimple;
